/**
 * *When they want it done* — one control everywhere (`docs/new-request.md`
 * §8.1, Board 6, Board 15 A).
 *
 * The ten chips the server listed for today (`whenChips`), one at a time,
 * wrapping; pressing the chip that is on clears it. *Pick a date* opens a
 * tinted row under them: **By · On · After · Between**, the date (two for
 * Between, joined by *and*), and how far off it is. The window itself is
 * worked out by `wantedWindow` — here for the words, on the server again when
 * the request is saved.
 */
import {
  dayComma, fromToday, PICK_MODES, PICK_MODE_LABELS,
  type PickMode, type WantedChoice, type WantedKind,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { DateField } from '../components/date-field.js'
import { IconCalendar } from '../frame/icons.js'
import type { JobFields } from './model.js'
import { useRequestSheet } from './sheet-context.js'
import { ModeTrack, PickChip, useCoarsePointer } from './job-kit.js'

const PICKED: readonly string[] = PICK_MODES

/**
 * What *When* sends: the choice, or null while *Pick a date* is still
 * waiting for its date — a By with no day is not a window yet, and the server
 * would rightly refuse it.
 */
export function wantedToSend(job: JobFields): WantedChoice | null {
  const wanted = job.wanted
  if (!wanted) return null
  if (wanted.kind === 'between') return wanted.date && wanted.date2 ? wanted : null
  if (PICKED.includes(wanted.kind)) return wanted.date ? wanted : null
  if (wanted.kind === 'season') return wanted.season ? wanted : null
  return wanted
}

/** The choice a chip makes (Board 6). */
function choiceOf(key: string): WantedChoice {
  if (key.startsWith('season:')) return { kind: 'season', season: key.slice('season:'.length) }
  if (key === 'pick') return { kind: 'by', date: null }
  return { kind: key as WantedKind }
}

export function WhenField({ phone }: { phone?: boolean }) {
  const api = useRequestSheet()
  const coarse = useCoarsePointer()
  const job = api.state.job
  const chips = api.setup.when
  const today = api.setup.today
  const picking = job.wantedChip === 'pick'
  const mode: PickMode = picking && job.wanted && PICKED.includes(job.wanted.kind)
    ? job.wanted.kind as PickMode : 'by'

  const setJob = (change: (job: JobFields) => JobFields) =>
    api.update((state) => ({ ...state, job: change(state.job) }))

  const press = (key: string) => setJob((current) => (current.wantedChip === key
    ? { ...current, wantedChip: null, wanted: null }
    : { ...current, wantedChip: key, wanted: choiceOf(key) }))

  const setMode = (next: PickMode) => setJob((current) => ({
    ...current,
    wanted: {
      kind: next,
      date: current.wanted?.date ?? null,
      date2: next === 'between' ? current.wanted?.date2 ?? null : null,
    },
  }))

  /* §19 B16: a Between whose second date is before the first swaps them. */
  const setDate = (which: 'date' | 'date2', value: string) => setJob((current) => {
    const wanted = { kind: mode as WantedKind, date: current.wanted?.date ?? null,
      date2: current.wanted?.date2 ?? null, [which]: value || null }
    if (wanted.kind === 'between' && wanted.date && wanted.date2 && wanted.date2 < wanted.date) {
      return { ...current, wanted: { ...wanted, date: wanted.date2, date2: wanted.date } }
    }
    return { ...current, wanted }
  })

  const first = job.wanted?.date ?? null
  const second = job.wanted?.date2 ?? null
  const native = phone || coarse

  return (
    <div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="When they want it done">
        {chips.map((chip) => (
          <PickChip key={chip.key} on={job.wantedChip === chip.key} onClick={() => press(chip.key)}
            phone={phone}>
            {chip.label}
          </PickChip>
        ))}
      </div>

      {picking && (
        <div className={cn('mt-2 flex flex-wrap items-center gap-2',
          phone ? '' : 'rounded-[10px] border border-line-faint bg-surface-alt p-2')}>
          <ModeTrack
            label="Pick a date"
            phone={phone}
            options={PICK_MODES.map((value) => ({ value, label: PICK_MODE_LABELS[value] }))}
            value={mode}
            onChange={setMode}
          />
          <DateBox native={native} phone={phone} value={first} label={PICK_MODE_LABELS[mode]}
            onChange={(value) => setDate('date', value)} />
          {mode === 'between' && (
            <>
              <span className="text-[12px] text-text-muted">and</span>
              <DateBox native={native} phone={phone} value={second} label="and"
                onChange={(value) => setDate('date2', value)} />
            </>
          )}
          {first && !phone && (
            <span className="whitespace-nowrap text-[11.5px] text-text-muted">{fromToday(first, today)}</span>
          )}
        </div>
      )}
      {picking && first && phone && (
        <span className="mt-1.5 block text-[11.5px] text-text-muted">{fromToday(first, today)}</span>
      )}
    </div>
  )
}

/**
 * The date: the app's date field with its calendar on a desktop, the system
 * picker on a phone or a touch screen (§8.1, §16.3).
 */
function DateBox({ native, phone, value, label, onChange }: {
  native: boolean
  phone?: boolean
  value: string | null
  label: string
  onChange: (value: string) => void
}) {
  if (!native) {
    return (
      <DateField value={value ?? ''} onChange={onChange} label={label} display="day"
        className="w-[150px]" />
    )
  }
  return (
    <label className={cn('relative inline-flex items-center gap-1.5 rounded-[10px] border border-line',
      'bg-surface px-2.5 font-semibold text-text',
      phone ? 'h-10 min-w-0 flex-1 text-[14px]' : 'h-8 text-[12.5px]')}>
      <span className="text-accent"><IconCalendar size={14} /></span>
      <span className={cn('truncate', !value && 'text-text-faint')}>{value ? dayComma(value) : 'Pick a date'}</span>
      <input
        type="date"
        aria-label={label}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </label>
  )
}
